import { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Table, Card, Badge, Spinner, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { db } from 'config/firebase';
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';

const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

export default function SKUDetailModal({ show, onHide, sku, timeRange = '90d' }) {
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [recentShipments, setRecentShipments] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);
  const [error, setError] = useState(null);
  const [analysisType, setAnalysisType] = useState('ALL'); // Order type filter
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange); // Time range filter

  // Fetch data when modal opens
  useEffect(() => {
    if (!show || !sku) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        // Fetch recommendation data from Firestore using composite document ID
        // Document IDs are formatted as: sku_analysisType_timeRange (e.g., "866426_ALL_30d")
        const docId = `${sku}_${analysisType}_${selectedTimeRange}`;
        const recDoc = await getDoc(doc(db, 'fulfillmentCostOpportunity', docId));

        if (recDoc.exists()) {
          const recData = recDoc.data();
          setRecommendation(recData);
          // Use sampleOrders from the stored document if available
          if (recData.sampleOrders?.length > 0) {
            setSampleOrders(recData.sampleOrders);
          }
        } else {
          setRecommendation(null);
        }

        // Fetch sample orders from backend API with selected filters
        try {
          const apiResponse = await fetch(
            `${CLOUD_FUNCTIONS_URL}/fcOptimizerSkuDetail/${encodeURIComponent(sku)}?analysisType=${analysisType}&timeRange=${selectedTimeRange}`
          );
          if (apiResponse.ok) {
            const apiData = await apiResponse.json();
            // Try fresh analysis first, then fall back to stored recommendation
            if (apiData.analysis?.sampleOrders?.length > 0) {
              setSampleOrders(apiData.analysis.sampleOrders);
            } else if (apiData.recommendation?.sampleOrders?.length > 0) {
              setSampleOrders(apiData.recommendation.sampleOrders);
            }
          }
        } catch (apiErr) {
          console.log('Could not fetch API data:', apiErr.message);
          setSampleOrders([]);
        }

        // Note: Fulfillments collection doesn't have sku field populated (data sync issue)
        // Use sampleOrders from API instead for shipment history
        setRecentShipments([]);

      } catch (err) {
        console.error('Error fetching SKU details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [show, sku, analysisType, selectedTimeRange]); // Re-fetch when analysisType or timeRange changes

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      dialogClassName="sku-detail-modal-90w"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon="box" className="me-2" />
          SKU Details: {sku}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Analysis Filters */}
        <Card className="mb-3 bg-light border-0">
          <Card.Body className="py-2">
            <Row className="align-items-center g-3">
              <Col xs="auto">
                <small className="text-muted fw-semi-bold">Order Type:</small>
              </Col>
              <Col xs="auto">
                <Form.Select
                  size="sm"
                  value={analysisType}
                  onChange={(e) => setAnalysisType(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="ALL">All Orders (Blended)</option>
                  <option value="SINGLE_SKU">SS1 Only (Single Unit)</option>
                  <option value="MULTI_SKU">SSN + MS (Multi-Unit/SKU)</option>
                </Form.Select>
              </Col>
              <Col xs="auto">
                <small className="text-muted fw-semi-bold">Time Range:</small>
              </Col>
              <Col xs="auto">
                <Form.Select
                  size="sm"
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="60d">Last 60 Days</option>
                  <option value="90d">Last 90 Days</option>
                </Form.Select>
              </Col>
              <Col>
                <small className="text-muted">
                  {analysisType === 'SINGLE_SKU' && '(Purest signal - single-unit orders only)'}
                  {analysisType === 'MULTI_SKU' && '(Multi-quantity + multi-SKU orders)'}
                  {analysisType === 'ALL' && '(Blended from all order types)'}
                </small>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading details...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <>
            {/* Recommendation Summary */}
            {recommendation ? (
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">Fulfillment Cost Analysis</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Table borderless size="sm" className="fs--1 mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted">Recommendation</td>
                            <td className="fw-semi-bold">
                              <RecommendationBadge value={recommendation.recommendation} />
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">Suggested Change</td>
                            <td className="fw-semi-bold text-primary">
                              ${recommendation.amount || recommendation.toBeFulfillmentCost || '-'}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">Sample Size</td>
                            <td>{recommendation.totalOrdersPresent || recommendation.sampleSize || '-'} orders</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <Table borderless size="sm" className="fs--1 mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted">Average Cost</td>
                            <td>${recommendation.averageFulfillmentCost || recommendation.avgCost || '-'}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Cost Range</td>
                            <td>
                              ${recommendation.minFulfillmentCost || recommendation.minCost || '-'} -
                              ${recommendation.maxFulfillmentCost || recommendation.maxCost || '-'}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">Std Deviation</td>
                            <td>{recommendation.stdDev ? `$${recommendation.stdDev}` : '-'}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <Alert variant="info">
                No recommendation data available for this SKU.
              </Alert>
            )}

            {/* Order Mix & FC Options */}
            {recommendation && (recommendation.orderMix || recommendation.fcOptions) && (
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">Order Mix & FC Options</h6>
                </Card.Header>
                <Card.Body>
                  <OrderMixFCOptions data={recommendation} />
                </Card.Body>
              </Card>
            )}

            {/* Calculation Breakdown */}
            {recommendation && (
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">Calculation Breakdown</h6>
                </Card.Header>
                <Card.Body>
                  <CalculationBreakdown data={recommendation} />
                </Card.Body>
              </Card>
            )}

            {/* Sample Orders - Shows all orders used in the analysis */}
            {sampleOrders.length > 0 && (
              <Card className="mb-3">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Sample Orders (Used in Analysis)</h6>
                  <span className="badge bg-primary">{sampleOrders.length} orders</span>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                    <Table hover size="sm" className="fs--1 mb-0">
                      <thead className="bg-200 sticky-top">
                        <tr>
                          <th>Order #</th>
                          <th>Date</th>
                          <th>Service</th>
                          <th className="text-end">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sampleOrders.map((order, idx) => (
                          <tr key={order.orderId || order.orderNumber || idx}>
                            <td>
                              <a
                                href={`/orders/${order.orderId || order.orderNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary"
                              >
                                {order.orderNumber || order.orderId || '-'}
                              </a>
                            </td>
                            <td>{formatDate(order.date)}</td>
                            <td>{order.serviceCode || '-'}</td>
                            <td className="text-end">
                              {order.cost ? `$${parseFloat(order.cost).toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Recent Shipments */}
            <Card>
              <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Recent Shipments</h6>
                <span className="badge bg-secondary">{recentShipments.length}</span>
              </Card.Header>
              <Card.Body className="p-0">
                {recentShipments.length > 0 ? (
                  <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                    <Table hover size="sm" className="fs--1 mb-0">
                      <thead className="bg-200 sticky-top">
                        <tr>
                          <th>Date</th>
                          <th>Order ID</th>
                          <th>Service</th>
                          <th className="text-end">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentShipments.map((shipment) => (
                          <tr key={shipment.id}>
                            <td>{formatDate(shipment.createDate)}</td>
                            <td>{shipment.orderId || '-'}</td>
                            <td>{shipment.serviceCode || '-'}</td>
                            <td className="text-end">
                              {shipment.shipmentCost ? `$${parseFloat(shipment.shipmentCost).toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon="truck" className="fs-3 mb-2" />
                    <p className="mb-0">No shipment history available</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RecommendationBadge({ value }) {
  const config = {
    'Increase': { variant: 'danger', icon: 'arrow-up' },
    'Decrease': { variant: 'success', icon: 'arrow-down' },
    'No Change': { variant: 'secondary', icon: 'minus' }
  };

  const c = config[value] || config['No Change'];

  return (
    <Badge bg={c.variant}>
      <FontAwesomeIcon icon={c.icon} className="me-1" />
      {value}
    </Badge>
  );
}

function CalculationBreakdown({ data }) {
  const avgCost = parseFloat(data.averageFulfillmentCost) || parseFloat(data.avgCost) || 0;
  const stdDev = parseFloat(data.stdDev) || 0;
  const sampleSize = parseInt(data.totalOrdersPresent) || parseInt(data.sampleSize) || 0;
  const cv = parseFloat(data.cv) || (avgCost > 0 ? stdDev / avgCost : 0);

  // Sophisticated strategy fields
  const ss1Count = parseInt(data.ss1Count) || 0;
  const economyPct = parseFloat(data.economyPct) || 0;
  const temperatureCategory = data.temperatureCategory || 'AMBIENT';
  const materialCost = parseFloat(data.materialCost) || 0;
  const handlingFee = parseFloat(data.handlingFee) || 0;
  const weightBand = data.weightBand || 'W4';
  const baseFC = parseFloat(data.baseFC) || 0;
  const aggressivenessScore = parseInt(data.aggressivenessScore) || 0;

  // Ground vs Expedited breakdown
  const groundSampleSize = parseInt(data.groundSampleSize) || 0;
  const expeditedSampleSize = parseInt(data.expeditedSampleSize) || 0;
  const groundAvgCost = parseFloat(data.groundAvgCost) || 0;
  const expeditedAvgCost = parseFloat(data.expeditedAvgCost) || 0;

  // Get strategy info from stored data, or calculate it
  const strategy = data.strategy || 'CONSERVATIVE';
  const strategyPercentile = data.strategyPercentile || 80;

  // Get actual percentiles from stored data
  const p65 = parseFloat(data.p65) || avgCost;
  const p70 = parseFloat(data.p70) || avgCost;
  const p75 = parseFloat(data.p75) || avgCost;
  const p80 = parseFloat(data.p80) || avgCost + (0.84 * stdDev);

  // Target FC from stored data
  const targetFC = parseFloat(data.targetFC) || parseFloat(data.suggestedFC) || Math.max(avgCost, p80);

  // Strategy badge colors
  const strategyColors = {
    AGGRESSIVE: 'success',
    MODERATE: 'info',
    BALANCED: 'primary',
    CONSERVATIVE: 'warning',
    PROFILE_BASED: 'secondary'
  };

  // Temperature category colors
  const tempCategoryColors = {
    AMBIENT: 'secondary',
    REFRIGERATED: 'info',
    FROZEN: 'primary'
  };

  return (
    <>
      {/* Data Quality Indicators */}
      <Row className="mb-3">
        <Col md={4}>
          <div className="border rounded p-2 text-center">
            <small className="text-muted d-block">Sample Size</small>
            <span className="fs-4 fw-bold">{sampleSize}</span>
            <small className="text-muted d-block">
              {ss1Count > 0 && <span className="text-success">{ss1Count} SS1</span>}
            </small>
          </div>
        </Col>
        <Col md={4}>
          <div className="border rounded p-2 text-center">
            <small className="text-muted d-block">Variability (CV)</small>
            <span className={`fs-4 fw-bold ${cv > 0.5 ? 'text-danger' : cv > 0.35 ? 'text-warning' : 'text-success'}`}>
              {(cv * 100).toFixed(1)}%
            </span>
            <small className="text-muted d-block">{cv <= 0.35 ? 'Low' : cv <= 0.5 ? 'Moderate' : 'High'}</small>
          </div>
        </Col>
        <Col md={4}>
          <div className="border rounded p-2 text-center">
            <small className="text-muted d-block">Economy Carrier %</small>
            <span className={`fs-4 fw-bold ${economyPct >= 50 ? 'text-success' : 'text-muted'}`}>
              {economyPct.toFixed(0)}%
            </span>
            <small className="text-muted d-block">{economyPct >= 50 ? 'High' : economyPct >= 30 ? 'Moderate' : 'Low'}</small>
          </div>
        </Col>
      </Row>

      {/* Ground vs Expedited Breakdown (if both exist) */}
      {(groundSampleSize > 0 || expeditedSampleSize > 0) && (
        <Row className="mb-3">
          <Col md={6}>
            <div className="border rounded p-2">
              <small className="text-muted d-block">Ground Shipments</small>
              <span className="fw-bold">{groundSampleSize} orders</span>
              {groundAvgCost > 0 && <span className="text-muted ms-2">(avg: ${groundAvgCost.toFixed(2)})</span>}
            </div>
          </Col>
          <Col md={6}>
            <div className="border rounded p-2">
              <small className="text-muted d-block">Expedited Shipments</small>
              <span className="fw-bold">{expeditedSampleSize} orders</span>
              {expeditedAvgCost > 0 && <span className="text-muted ms-2">(avg: ${expeditedAvgCost.toFixed(2)})</span>}
            </div>
          </Col>
        </Row>
      )}

      <Table borderless size="sm" className="fs--1 mb-0">
        <tbody>
          {/* Strategy Section */}
          <tr className="bg-light">
            <td colSpan={3} className="fw-semi-bold">Strategy Selection</td>
          </tr>
          <tr>
            <td className="text-muted" style={{ width: '40%' }}>Pricing Strategy</td>
            <td className="text-end">
              <Badge bg={strategyColors[strategy] || 'secondary'}>
                {strategy}
              </Badge>
              {strategyPercentile && <small className="text-muted ms-2">(P{strategyPercentile})</small>}
            </td>
            <td className="text-muted fs--2">Based on SS1, CV, Economy %</td>
          </tr>
          <tr>
            <td className="text-muted">Aggressiveness Score</td>
            <td className="text-end">
              <span className={aggressivenessScore >= 70 ? 'text-success fw-bold' : aggressivenessScore >= 40 ? 'text-warning' : 'text-muted'}>
                {aggressivenessScore}/100
              </span>
            </td>
            <td className="text-muted fs--2">5-factor composite score</td>
          </tr>
          <tr>
            <td className="text-muted">SS1 (Pure Signal) Count</td>
            <td className="text-end">{ss1Count} orders</td>
            <td className="text-muted fs--2">Single-SKU, Single-Qty</td>
          </tr>

          {/* Percentiles Section */}
          <tr className="bg-light">
            <td colSpan={3} className="fw-semi-bold">Percentile Analysis</td>
          </tr>
          <tr>
            <td className="text-muted">Percentiles</td>
            <td className="text-end">
              <span className={strategyPercentile === 65 ? 'fw-bold text-success' : ''}>P65: ${p65.toFixed(2)}</span>
              {' | '}
              <span className={strategyPercentile === 70 ? 'fw-bold text-info' : ''}>P70: ${p70.toFixed(2)}</span>
              {' | '}
              <span className={strategyPercentile === 75 ? 'fw-bold text-primary' : ''}>P75: ${p75.toFixed(2)}</span>
              {' | '}
              <span className={strategyPercentile === 80 ? 'fw-bold text-warning' : ''}>P80: ${p80.toFixed(2)}</span>
            </td>
            <td className="text-muted fs--2">P{strategyPercentile} selected</td>
          </tr>

          {/* Category-Aware FC Section */}
          <tr className="bg-light">
            <td colSpan={3} className="fw-semi-bold">Category-Aware FC Calculation</td>
          </tr>
          <tr>
            <td className="text-muted">Temperature Category</td>
            <td className="text-end">
              <Badge bg={tempCategoryColors[temperatureCategory] || 'secondary'}>
                {temperatureCategory}
              </Badge>
            </td>
            <td className="text-muted fs--2">{materialCost > 0 ? `+$${materialCost} material` : 'No material cost'}</td>
          </tr>
          <tr>
            <td className="text-muted">Weight Band</td>
            <td className="text-end">{weightBand}</td>
            <td className="text-muted fs--2">+${handlingFee.toFixed(2)} handling</td>
          </tr>

          {/* Final Calculation */}
          <tr className="bg-light">
            <td colSpan={3} className="fw-semi-bold">Final Calculation</td>
          </tr>
          <tr>
            <td className="text-muted">Base FC (P{strategyPercentile})</td>
            <td className="text-end">${baseFC > 0 ? baseFC.toFixed(2) : p80.toFixed(2)}</td>
            <td className="text-muted fs--2">max(avg, percentile)</td>
          </tr>
          {materialCost > 0 && (
            <tr>
              <td className="text-muted">+ Material Cost</td>
              <td className="text-end">${materialCost.toFixed(2)}</td>
              <td className="text-muted fs--2">{temperatureCategory === 'FROZEN' ? 'Cooler + dry ice' : 'Cooler + ice packs'}</td>
            </tr>
          )}
          <tr>
            <td className="text-muted">+ Handling Fee</td>
            <td className="text-end">${handlingFee.toFixed(2)}</td>
            <td className="text-muted fs--2">Weight band: {weightBand}</td>
          </tr>
          <tr className="border-top">
            <td className="fw-bold">Target FC</td>
            <td className="text-end fw-bold text-primary fs-5">${targetFC.toFixed(2)}</td>
            <td className="text-muted fs--2">= Base + Material + Handling</td>
          </tr>
        </tbody>
      </Table>
    </>
  );
}

/**
 * Order Mix & FC Options component
 * Shows order type distribution and all available FC calculation methods
 */
function OrderMixFCOptions({ data }) {
  const orderMix = data.orderMix || {};
  const fcOptions = data.fcOptions || {};

  const ss1Pct = orderMix.ss1Pct || 0;
  const ssnPct = orderMix.ssnPct || 0;
  const msPct = orderMix.msPct || 0;
  const totalOrders = orderMix.totalOrders || 0;

  const hasOrderMix = ss1Pct > 0 || ssnPct > 0 || msPct > 0;
  const hasFCOptions = fcOptions.ss1 || fcOptions.ssn || fcOptions.ms || fcOptions.blended;

  if (!hasOrderMix && !hasFCOptions) {
    return (
      <div className="text-center py-3 text-muted">
        <FontAwesomeIcon icon="chart-pie" className="fs-3 mb-2" />
        <p className="mb-0">Order mix data not yet available. Run BigQuery sync to populate.</p>
      </div>
    );
  }

  return (
    <Row>
      {/* Order Mix Breakdown */}
      <Col md={6}>
        <h6 className="text-muted mb-2">Order Type Mix ({totalOrders} orders)</h6>
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="fs--1">
              <Badge bg="success" className="me-2">SS1</Badge>
              Single-Unit
            </span>
            <span className="fw-semi-bold">{ss1Pct.toFixed(1)}%</span>
          </div>
          <div className="progress" style={{ height: '6px' }}>
            <div
              className="progress-bar bg-success"
              style={{ width: `${ss1Pct}%` }}
            />
          </div>
        </div>
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="fs--1">
              <Badge bg="info" className="me-2">SSN</Badge>
              Multi-Quantity
            </span>
            <span className="fw-semi-bold">{ssnPct.toFixed(1)}%</span>
          </div>
          <div className="progress" style={{ height: '6px' }}>
            <div
              className="progress-bar bg-info"
              style={{ width: `${ssnPct}%` }}
            />
          </div>
        </div>
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="fs--1">
              <Badge bg="warning" className="me-2">MS</Badge>
              Multi-SKU
            </span>
            <span className="fw-semi-bold">{msPct.toFixed(1)}%</span>
          </div>
          <div className="progress" style={{ height: '6px' }}>
            <div
              className="progress-bar bg-warning"
              style={{ width: `${msPct}%` }}
            />
          </div>
        </div>

        {/* Blended FC Recommendation */}
        {data.isBlendedRecommended && (
          <Alert variant="info" className="mt-3 py-2 fs--1">
            <FontAwesomeIcon icon="lightbulb" className="me-2" />
            <strong>Tip:</strong> This SKU is frequently ordered in multi-unit or bundled orders.
            Consider using Blended FC for more accurate pricing.
          </Alert>
        )}
      </Col>

      {/* FC Options */}
      <Col md={6}>
        <h6 className="text-muted mb-2">FC Calculation Options</h6>
        <Table size="sm" className="fs--1 mb-0">
          <tbody>
            {fcOptions.ss1 && (
              <tr className={data.recommendedFCOption === 'SS1' ? 'table-success' : ''}>
                <td>
                  <Badge bg="success" className="me-1">SS1</Badge>
                  {data.recommendedFCOption === 'SS1' && (
                    <FontAwesomeIcon icon="star" className="text-warning ms-1" title="Recommended" />
                  )}
                </td>
                <td className="fw-semi-bold text-end">${fcOptions.ss1.fc?.toFixed(2) || '-'}</td>
                <td className="text-muted text-end">{fcOptions.ss1.source}</td>
              </tr>
            )}
            {fcOptions.ssn?.fc && (
              <tr>
                <td><Badge bg="info">SSN</Badge></td>
                <td className="fw-semi-bold text-end">${fcOptions.ssn.fc.toFixed(2)}</td>
                <td className="text-muted text-end">{fcOptions.ssn.source}</td>
              </tr>
            )}
            {fcOptions.ms?.fc && (
              <tr>
                <td><Badge bg="warning">MS</Badge></td>
                <td className="fw-semi-bold text-end">${fcOptions.ms.fc.toFixed(2)}</td>
                <td className="text-muted text-end">{fcOptions.ms.source}</td>
              </tr>
            )}
            {fcOptions.blended?.fc && (
              <tr className={data.recommendedFCOption === 'BLENDED' ? 'table-primary' : ''}>
                <td>
                  <Badge bg="primary">Blended</Badge>
                  {data.recommendedFCOption === 'BLENDED' && (
                    <FontAwesomeIcon icon="star" className="text-warning ms-1" title="Recommended" />
                  )}
                </td>
                <td className="fw-semi-bold text-end">${fcOptions.blended.fc.toFixed(2)}</td>
                <td className="text-muted text-end">{fcOptions.blended.source}</td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Blended FC Breakdown */}
        {data.blendedFCBreakdown && (
          <div className="mt-2 p-2 bg-light rounded fs--2">
            <strong>Blended breakdown:</strong>
            <div className="text-muted">
              SS1: ${data.blendedFCBreakdown.ss1Component?.toFixed(2) || '0.00'} +
              SSN: ${data.blendedFCBreakdown.ssnComponent?.toFixed(2) || '0.00'} +
              MS: ${data.blendedFCBreakdown.msComponent?.toFixed(2) || '0.00'}
            </div>
          </div>
        )}
      </Col>
    </Row>
  );
}

function formatDate(dateValue) {
  if (!dateValue) return '-';

  try {
    // Handle Firestore Timestamp
    if (dateValue.toDate) {
      return dateValue.toDate().toLocaleDateString();
    }
    // Handle string date
    return new Date(dateValue).toLocaleDateString();
  } catch {
    return '-';
  }
}
